import { useState } from 'react';
import { Button, Form, Row, FormItem, Input, Password, SubTitle, Title, Checkbox } from '@/components/atoms';
import { List, Modal, Result, Spin, Typography } from 'antd';
import api from '@/api';
import { API_AUTH_CHECK, PATH_AUTH_LOGIN, PATH_AUTH_SIGNUP, PATH_TERMS_OF_PRIVACY, PATH_TERMS_OF_SERVICE } from '@/constants/constant';
import { Link } from 'react-router-dom';
import { Store } from 'antd/lib/form/interface';
import { ErrorMessage } from '@/constants/message';
import HttpStatus from 'http-status-codes';
import SignupStep, { allCases, describeAt } from '@/components/pages/auth/signup/SignupStep';
import AccountType from '@/components/pages/auth/signup/AccountType';
import { TermsOfPrivacy, TermsOfService } from '@/components/organisms';

type SignupPageStateType = {
  loading: boolean;
  success: boolean;
  step: number;
  accountType: AccountType;
};

/** @link PATH_AUTH_SIGNUP */
const SignupPage = () => {
  const [form] = Form.useForm();
  const [state, setState] = useState<SignupPageStateType>({
    loading: false,
    success: false,
    step: allCases[0],
    accountType: AccountType.BUSINESS,
  });

  const [isTermsOfServiceVisible, setTermsOfServiceVisible] = useState(false);
  const [isTermsOfPrivacyVisible, setTermsOfPrivacyVisible] = useState(false);

  const onFinish = (values: Store) => {
    setState({ ...state, loading: true });
    api
      .post(PATH_AUTH_SIGNUP, 'pauth.PbSignUpRequest', { ...values, accountType: state.accountType })
      .then(() => {
        setState({ ...state, success: true, step: SignupStep.COMPLETE, loading: false });
      })
      .catch(() => {
        setState({ ...state, loading: false });
      });
  };

  const layoutAt = (step: number) => {
    switch (step) {
      case SignupStep.AGREE_TERMS:
        return (
          <Button block type="success" size="large" onClick={() => setState({ ...state, step: SignupStep.SELECT_TYPE })}>
            동의합니다.
          </Button>
        );
      case SignupStep.SELECT_TYPE:
        return (
          <List>
            <List.Item onClick={() => setState({ ...state, accountType: 10, step: SignupStep.INPUT_INFOS })} className="process-list">
              <List.Item.Meta title="B2B" description="회원가입시에 입력한 전화번호로 인증합니다." />
            </List.Item>
          </List>
        );
      case SignupStep.INPUT_INFOS:
        return (
          <Form onFinish={onFinish} layout="vertical" form={form}>
            <FormItem
              label="이메일"
              name="email"
              hasFeedback
              validateFirst
              rules={[
                { required: true, message: '이메일을 입력하세요.' },
                { type: 'email', message: '잘못된 이메일 형식입니다.' },
                ({ getFieldError, setFields }) => ({
                  validator: async (rule, value, callback) => {
                    if (value && getFieldError('email').length === 0) {
                      setFields([{ name: 'email', validating: true }]);
                      api
                        .post(API_AUTH_CHECK, 'pauth.PbCheckRequest', { type: 'email', value })
                        .then(() => callback())
                        .catch((e) => {
                          setFields([{ name: 'email', validating: false }]);
                          switch (e.response.status) {
                            case HttpStatus.CONFLICT:
                              setFields([{ name: 'email', errors: [ErrorMessage.auth.email.already] }]);
                              break;
                            default:
                              break;
                          }
                          callback('Already used E-mail');
                        })
                        .finally(() => setFields([{ name: 'email', validating: false }]));
                    } else {
                      callback('aaa');
                    }
                  },
                }),
              ]}
            >
              <Input placeholder="이메일" />
            </FormItem>
            <FormItem
              label="비밀번호"
              name="password"
              rules={[
                { required: true, message: '비밀번호를 입력하세요.' },
                { min: 6, message: '6자 이상 입력하세요.' },
              ]}
            >
              <Password placeholder="비밀번호" />
            </FormItem>
            <FormItem
              label="이름"
              name="name"
              rules={[
                { required: true, message: '이름을 입력하세요.' },
                { min: 2, message: '2자 이상 입력하세요.' },
                { pattern: /^[가-힣]+$/, message: '한글만 입력할 수 있습니다.' },
              ]}
            >
              <Input placeholder="이름" />
            </FormItem>
            <FormItem
              label="핸드폰번호"
              name="mobile"
              rules={[
                { required: true, message: '핸드폰 번호를 입력하세요.' },
                { pattern: /^01(?:0|1|[6-9])[.-]?(\d{3}|\d{4})[.-]?(\d{4})$/, message: '잘못된 형식입니다.' },
              ]}
              style={{ marginBottom: '10px' }}
            >
              <Input placeholder="핸드폰 번호( - 제외 )" />
            </FormItem>
            <FormItem name="agreement" valuePropName="checked">
              <Checkbox className="agreement-check">
                <Typography.Link onClick={() => setTermsOfPrivacyVisible(true)}>서비스 이용약관</Typography.Link> 및{' '}
                <Typography.Link onClick={() => setTermsOfPrivacyVisible(true)}>개인정보취급방침</Typography.Link>을 확인하였고 동의합니다.
              </Checkbox>
            </FormItem>
            <FormItem>
              <Button
                type="success"
                size="large"
                htmlType="submit"
                block
                loading={state.loading}
                disabled={state.loading}
                style={{ margin: '10px auto 0' }}
              >
                회원가입
              </Button>
            </FormItem>

            <Row justify="center">
              <Link className="link success-link" to={PATH_AUTH_LOGIN}>
                로그인 바로가기
              </Link>
            </Row>
          </Form>
        );
      case SignupStep.COMPLETE:
        return (
          <Result
            status="success"
            title="회원 가입이 완료되었습니다."
            subTitle="로그인하세요."
            extra={[
              <Button type="success" key="login">
                <Link to={PATH_AUTH_LOGIN}>로그인</Link>
              </Button>,
            ]}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Spin spinning={state.loading}>
      <Row justify="center" align="middle" style={{ flexDirection: 'column' }}>
        <Title>회원가입</Title>
        <SubTitle>{describeAt(state.step)}</SubTitle>
      </Row>
      {layoutAt(state.step)}
      <Modal visible={isTermsOfServiceVisible} onCancel={() => setTermsOfServiceVisible(false)} footer={null} width={800} style={{ height: 800 }}>
        <TermsOfService />
      </Modal>
      <Modal visible={isTermsOfPrivacyVisible} onCancel={() => setTermsOfPrivacyVisible(false)} footer={null} width={800}>
        <TermsOfPrivacy />
      </Modal>
    </Spin>
  );
};

export default SignupPage;
